import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

const TOKEN_KEY = 'dehc_admin_token';
const REFRESH_KEY = 'dehc_admin_refresh';

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored) {
      try {
        const payload = JSON.parse(atob(stored.split('.')[1]));
        if (payload.exp * 1000 > Date.now()) {
          setToken(stored);
        } else {
          refreshToken().catch(() => {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_KEY);
          });
        }
      } catch {
        localStorage.removeItem(TOKEN_KEY);
      }
    }
    setLoading(false);
  }, []);

  const refreshToken = async () => {
    const refresh = localStorage.getItem(REFRESH_KEY);
    if (!refresh) throw new Error('No refresh token');

    const region = import.meta.env.VITE_REGION;
    const clientId = import.meta.env.VITE_CLIENT_ID;

    const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
      },
      body: JSON.stringify({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: clientId,
        AuthParameters: {
          REFRESH_TOKEN: refresh,
        },
      }),
    });

    if (!response.ok) throw new Error('Refresh failed');

    const data = await response.json();
    const newToken = data.AuthenticationResult.AccessToken;
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
  };

  const login = useCallback(async (email, password) => {
    const region = import.meta.env.VITE_REGION;
    const clientId = import.meta.env.VITE_CLIENT_ID;

    const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
      },
      body: JSON.stringify({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: clientId,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      }),
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message || 'Authentication failed');
    }

    const data = await response.json();

    if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
      const err = new Error('PASSWORD_CHANGE_REQUIRED');
      err.session = data.Session;
      throw err;
    }

    const accessToken = data.AuthenticationResult.AccessToken;
    const refreshTkn = data.AuthenticationResult.RefreshToken;

    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshTkn) localStorage.setItem(REFRESH_KEY, refreshTkn);
    setToken(accessToken);
  }, []);

  const completeNewPassword = useCallback(async (email, newPassword, session) => {
    const region = import.meta.env.VITE_REGION;
    const clientId = import.meta.env.VITE_CLIENT_ID;

    const response = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': 'AWSCognitoIdentityProviderService.RespondToAuthChallenge',
      },
      body: JSON.stringify({
        ChallengeName: 'NEW_PASSWORD_REQUIRED',
        ClientId: clientId,
        ChallengeResponses: {
          USERNAME: email,
          NEW_PASSWORD: newPassword,
        },
        Session: session,
      }),
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message || 'Password change failed');
    }

    const data = await response.json();
    const accessToken = data.AuthenticationResult.AccessToken;
    const refreshTkn = data.AuthenticationResult.RefreshToken;

    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshTkn) localStorage.setItem(REFRESH_KEY, refreshTkn);
    setToken(accessToken);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    setToken(null);
  }, []);

  return (
    <AuthContext.Provider value={{ token, loading, login, logout, completeNewPassword }}>
      {children}
    </AuthContext.Provider>
  );
}
